import { useEffect, useRef, useSyncExternalStore } from 'react';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { RealtimeMessage } from '@cloudsign/shared';
import type { RealtimeControl } from '@cloudsign/shared';

/**
 * Live updates (ADR 0007). One Socket.IO connection per signed-in tab. Pages call `useLiveChannel`;
 * they never name rooms. On reconnect the page refetches its REST snapshot (`onReconnect`), then keeps
 * applying messages, which are idempotent upserts.
 */

export type LiveHandler = (data: unknown, message: RealtimeMessage) => void;

interface Sub {
  channel: string;
  siteId: string;
  onMessage: LiveHandler;
  onReconnect?: () => void;
}

/** What the hub needs from a connection: the real socket or the in-memory fake. */
export interface LiveTransport {
  open(): void;
  close(): void;
  subscribe(channel: string, siteId: string): void;
  unsubscribe(channel: string, siteId: string): void;
}

export interface TransportEvents {
  connected(reconnect: boolean): void;
  disconnected(): void;
  message(raw: unknown): void;
  control(c: RealtimeControl): void;
}

const CLOSE_DELAY_MS = 2000;
const SEP = '\u0000';
const keyOf = (channel: string, siteId: string): string => `${channel}${SEP}${siteId}`;

/** Holds the subscriptions and the connected flag; the transport does the sending. */
export class LiveHub {
  private subs = new Set<Sub>();
  private counts = new Map<string, number>();
  private listeners = new Set<() => void>();
  private transport: LiveTransport | null = null;
  private everConnected = false;
  private isConnected = false;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly makeTransport: (events: TransportEvents) => LiveTransport) {}

  readonly getConnected = (): boolean => this.isConnected;

  readonly onChange = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  private setConnected(v: boolean): void {
    if (this.isConnected === v) return;
    this.isConnected = v;
    for (const l of this.listeners) l();
  }

  private events: TransportEvents = {
    connected: (reconnect) => {
      const again = reconnect || this.everConnected;
      this.everConnected = true;
      // Re-join every room (the server forgets them when the socket drops), then tell pages to refetch.
      for (const k of this.counts.keys()) {
        const [channel, siteId] = k.split(SEP);
        if (channel && siteId) this.transport?.subscribe(channel, siteId);
      }
      this.setConnected(true);
      if (again) for (const s of [...this.subs]) s.onReconnect?.();
    },
    disconnected: () => this.setConnected(false),
    message: (raw) => {
      const parsed = RealtimeMessage.safeParse(raw);
      if (!parsed.success || !parsed.data.siteId) return;
      const m = parsed.data;
      for (const s of [...this.subs]) {
        if (s.channel === m.channel && s.siteId === m.siteId) s.onMessage(m.data, m);
      }
    },
    control: () => {
      // The server closes the socket after this; pages refetch when it returns.
      this.setConnected(false);
    },
  };

  /** Registers a subscription. Returns the function that removes it. */
  add(sub: Sub): () => void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    this.subs.add(sub);
    const k = keyOf(sub.channel, sub.siteId);
    const n = this.counts.get(k) ?? 0;
    this.counts.set(k, n + 1);
    if (!this.transport) {
      this.transport = this.makeTransport(this.events);
      this.transport.open();
    } else if (n === 0 && this.isConnected) {
      this.transport.subscribe(sub.channel, sub.siteId);
    }
    return () => {
      this.subs.delete(sub);
      const left = (this.counts.get(k) ?? 1) - 1;
      if (left > 0) {
        this.counts.set(k, left);
        return;
      }
      this.counts.delete(k);
      if (this.isConnected) this.transport?.unsubscribe(sub.channel, sub.siteId);
      if (this.subs.size === 0) {
        // Keep the socket a moment so switching site or page does not drop and redial it.
        this.closeTimer = setTimeout(() => {
          this.closeTimer = null;
          if (this.subs.size > 0) return;
          this.transport?.close();
          this.transport = null;
          this.everConnected = false;
          this.setConnected(false);
        }, CLOSE_DELAY_MS);
      }
    };
  }
}

// ---------- Real transport ----------

function socketTransport(events: TransportEvents): LiveTransport {
  let socket: Socket | null = null;
  let first = true;
  return {
    open() {
      socket = io({ path: '/api/socket.io', withCredentials: true });
      socket.on('connect', () => {
        events.connected(!first);
        first = false;
      });
      socket.on('disconnect', () => events.disconnected());
      socket.on('message', (raw: unknown) => events.message(raw));
      socket.on('control', (c: RealtimeControl) => events.control(c));
    },
    close() {
      socket?.disconnect();
      socket = null;
    },
    subscribe(channel, siteId) {
      socket?.emit('subscribe', { channel, siteId }, () => undefined);
    },
    unsubscribe(channel, siteId) {
      socket?.emit('unsubscribe', { channel, siteId }, () => undefined);
    },
  };
}

// ---------- Mock transport (VITE_MOCK=1 and tests) ----------

const mockState: { events: TransportEvents | null; subscribed: Set<string> } = { events: null, subscribed: new Set() };

function mockTransport(events: TransportEvents): LiveTransport {
  return {
    open() {
      mockState.events = events;
      // Connect on the next tick, like a real socket would.
      queueMicrotask(() => {
        if (mockState.events === events) events.connected(false);
      });
    },
    close() {
      if (mockState.events === events) mockState.events = null;
      mockState.subscribed.clear();
    },
    subscribe(channel, siteId) {
      mockState.subscribed.add(keyOf(channel, siteId));
    },
    unsubscribe(channel, siteId) {
      mockState.subscribed.delete(keyOf(channel, siteId));
    },
  };
}

/** MSW handlers and tests push fake live messages through this. */
export const mockLive = {
  /** Delivers a message to pages subscribed to the channel and site (like the server's `message` event). */
  emit(channel: string, siteId: string, data: unknown): void {
    mockState.events?.message({ channel, siteId, data, at: new Date().toISOString() });
  },
  /** Simulates a dropped connection. */
  disconnect(): void {
    mockState.events?.disconnected();
  },
  /** Simulates the connection coming back; pages refetch. */
  reconnect(): void {
    mockState.events?.connected(true);
  },
  /** True when a page has joined this channel and site. */
  isSubscribed(channel: string, siteId: string): boolean {
    return mockState.subscribed.has(keyOf(channel, siteId));
  },
};

const useMock = import.meta.env.VITE_MOCK === '1' || import.meta.env.MODE === 'test';
export const liveHub = new LiveHub(useMock ? mockTransport : socketTransport);

/**
 * Subscribes the page to a live channel for one site while it is mounted.
 * `onMessage` gets the channel's payload; `onReconnect` fires after a dropped connection returns, so the
 * page can refetch its REST snapshot. Returns whether the connection is up.
 */
export function useLiveChannel(
  channel: string,
  siteId: string | null | undefined,
  onMessage: LiveHandler,
  onReconnect?: () => void,
): { connected: boolean } {
  const messageRef = useRef(onMessage);
  const reconnectRef = useRef(onReconnect);
  useEffect(() => {
    messageRef.current = onMessage;
    reconnectRef.current = onReconnect;
  });
  useEffect(() => {
    if (!siteId) return;
    return liveHub.add({
      channel,
      siteId,
      onMessage: (d, m) => messageRef.current(d, m),
      onReconnect: () => reconnectRef.current?.(),
    });
  }, [channel, siteId]);
  const connected = useSyncExternalStore(liveHub.onChange, liveHub.getConnected, () => false);
  return { connected };
}
